/**
 * LEKI: Mark Unavailable Variant Handler
 *
 * Reads a supplementary JSON tag (data-mark-unavailable-json) injected by Liquid
 * containing an array of variant IDs that have the custom.mark_unavailable metafield
 * set to true. For those variants:
 *   - Adds "sold-out" CSS class to the corresponding option pill/swatch
 *   - Adds "variant--mark-unavailable" class to the form wrapper
 *   - Changes the Add to Cart button label to "Currently Unavailable"
 *   - Disables the button
 *   - Hides the Back In Stock (BIS) notify button
 *
 * Uses requestAnimationFrame so our overrides run AFTER theme.js's synchronous
 * SelloutVariants and ProductAddForm handlers have already applied their changes.
 *
 * Also uses MutationObserver to handle dynamically injected Quick View content.
 */

(function () {
  'use strict';

  var UNAVAILABLE_LABEL = 'Currently Unavailable';

  // ─── Helpers ────────────────────────────────────────────────────────────────

  /** Parse mark_unavailable variant IDs from the supplementary JSON tag */
  function getMarkIds(container) {
    var el = container.querySelector('[data-mark-unavailable-json]');
    if (!el) return [];
    try {
      var ids = JSON.parse(el.textContent.trim());
      return Array.isArray(ids) ? ids : [];
    } catch (e) {
      return [];
    }
  }

  /** Parse the full variant list from the embedded product JSON */
  function getVariants(container) {
    var el = container.querySelector('[data-product-json]');
    if (!el) return [];
    try {
      var data = JSON.parse(el.textContent);
      return (data && Array.isArray(data.variants)) ? data.variants : [];
    } catch (e) {
      return [];
    }
  }

  /**
   * Read currently selected option values from radio inputs.
   * Returns an array indexed by option position (0-based).
   */
  function getSelections(container) {
    var selections = [];
    container.querySelectorAll('[data-single-option-selector]').forEach(function (input) {
      // Skip unchecked radios; include checked radios and hidden inputs
      if (input.type === 'radio' && !input.checked) return;
      var indexAttr = input.getAttribute('data-index');
      if (!indexAttr) return;
      var idx = parseInt(indexAttr.replace('option', ''), 10) - 1;
      if (!isNaN(idx)) selections[idx] = input.value;
    });
    return selections;
  }

  /** Find the variant that matches a given set of selections */
  function findVariant(variants, selections) {
    return variants.find(function (v) {
      return v.options.every(function (opt, i) {
        return opt === selections[i];
      });
    });
  }

  // ─── Core update functions ───────────────────────────────────────────────────

  /**
   * For each option element in the container, check whether selecting it
   * (while keeping other options the same) would result in a mark_unavailable
   * variant. If so, add the "sold-out" class to its parent element.
   *
   * Mirrors the logic of theme.js's SelloutVariants.update() but for the
   * mark_unavailable flag rather than variant.available.
   */
  function updateSoldOutClasses(container) {
    var markIds = getMarkIds(container);
    if (!markIds.length) return;

    var variants = getVariants(container);
    var currentSelections = getSelections(container);

    container.querySelectorAll('[data-option-position]').forEach(function (optionWrapper) {
      var position = parseInt(optionWrapper.getAttribute('data-option-position'), 10) - 1;
      if (isNaN(position)) return;

      optionWrapper.querySelectorAll('[data-single-option-selector]').forEach(function (input) {
        var val = input.value || input.getAttribute('data-value');
        if (!val) return;

        var testSelections = currentSelections.slice();
        testSelections[position] = val;

        var found = findVariant(variants, testSelections);
        if (found && markIds.indexOf(found.id) !== -1) {
          input.parentElement.classList.add('sold-out');
        }
      });
    });
  }

  /**
   * If the currently selected variant is mark_unavailable, apply the
   * "Currently Unavailable" state to the button and form wrapper.
   * Also hides the BIS (Back In Stock) notify button.
   */
  function updateButtonState(container) {
    var markIds = getMarkIds(container);
    if (!markIds.length) return;

    var variants = getVariants(container);
    var selections = getSelections(container);
    var currentVariant = findVariant(variants, selections);

    var isMarkUnavailable = currentVariant && markIds.indexOf(currentVariant.id) !== -1;

    // Form wrapper class
    var formWrapper = container.querySelector('[data-form-wrapper]');
    if (formWrapper) {
      if (isMarkUnavailable) {
        formWrapper.classList.add('variant--mark-unavailable');
      } else {
        formWrapper.classList.remove('variant--mark-unavailable');
      }
    }

    // Inventory countdown ("Hurry! Low inventory" etc.)
    // The snippet can render inside the form wrapper OR as a standalone block
    // elsewhere in the section — querySelectorAll on the section container catches both.
    container.querySelectorAll('[data-remaining-wrapper]').forEach(function (el) {
      el.style.display = isMarkUnavailable ? 'none' : '';
    });

    if (!isMarkUnavailable) return;

    // Button text
    var textEl = container.querySelector('[data-add-to-cart-text]');
    if (textEl && textEl.innerHTML !== UNAVAILABLE_LABEL) {
      textEl.innerHTML = UNAVAILABLE_LABEL;
    }

    // Disable button
    var btnEl = container.querySelector('[data-add-to-cart]');
    if (btnEl) {
      btnEl.disabled = true;
    }

    // Hide BIS (Back In Stock) notify button
    var bisBtn = container.querySelector('#BIS_trigger, .BTS_product_button');
    if (bisBtn) {
      bisBtn.style.display = 'none';
    }

    // Hide Buy It Now button
    var buyItNow = container.querySelector('[data-buy-it-now]');
    if (buyItNow) {
      buyItNow.classList.add('hidden');
    }
  }

  // ─── Container initialisation ────────────────────────────────────────────────

  var initializedContainers = new WeakSet();

  /**
   * Initialise mark-unavailable handling for a product form container.
   * Applies initial state and registers a change listener.
   */
  function initContainer(container) {
    if (initializedContainers.has(container)) return;
    if (!getMarkIds(container).length) return;

    initializedContainers.add(container);

    // Apply initial state immediately
    updateSoldOutClasses(container);
    updateButtonState(container);

    // On variant option change, re-apply after theme.js has run
    container.addEventListener('change', function (e) {
      if (!e.target.matches('[data-single-option-selector]')) return;
      requestAnimationFrame(function () {
        updateSoldOutClasses(container);
        updateButtonState(container);
      });
    });
  }

  /** Find all product containers within a root element and initialise them */
  function initContainersIn(root) {
    // Product Display Page containers
    root.querySelectorAll('[data-section-id]').forEach(initContainer);
    // Quick View popup containers
    root.querySelectorAll('[data-quick-view-inner]').forEach(initContainer);
    // Fallback: root itself may be the container
    if (root.matches && (root.matches('[data-section-id]') || root.matches('[data-quick-view-inner]'))) {
      initContainer(root);
    }
  }

  // ─── DOM Ready ───────────────────────────────────────────────────────────────

  function onDOMReady() {
    initContainersIn(document);
  }

  // With `defer`, scripts execute when readyState is 'interactive' — BEFORE
  // DOMContentLoaded fires. Checking for 'loading' would never be true and the
  // else branch would fire immediately, running our init before theme.js has had
  // its own DOMContentLoaded listener called (which resets button text and
  // sold-out classes). By flipping the check to 'complete' we ensure we always
  // register a listener when the page is still initialising, so our handler fires
  // AFTER theme.js's handler (theme.js registered first → runs first).
  if (document.readyState === 'complete') {
    onDOMReady();
  } else {
    document.addEventListener('DOMContentLoaded', onDOMReady);
  }

  // ─── MutationObserver for Quick View (dynamically injected content) ──────────

  var observer = new MutationObserver(function (mutations) {
    mutations.forEach(function (mutation) {
      mutation.addedNodes.forEach(function (node) {
        if (node.nodeType !== 1) return; // elements only
        // Check whether this node contains mark_unavailable JSON (= it's a product container)
        if (node.querySelector && node.querySelector('[data-mark-unavailable-json]')) {
          // Give theme.js time to initialise the new content first
          requestAnimationFrame(function () {
            initContainersIn(node);
          });
        }
      });
    });
  });

  observer.observe(document.body, { childList: true, subtree: true });

}());
